import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CommentOutlined,
  InboxOutlined,
  StopOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { Breadcrumb, Button, Card, Col, Flex, Input, Row, Select, Skeleton, Tag, Typography, message } from "antd";
import { useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import ClinicianAvatarCell from "../../components/Compliance/ClinicianAvatarCell.component";
import Encounter from "../../components/Compliance/Encounter.component";
import UppercaseLabel from "../../components/Compliance/UppercaseLabel.component";
import {
  SEVERITY_META,
  SLA_DAYS,
  STATUS_META,
  SeverityTag,
  type Status,
  StatusTag,
  TONE,
  ageDays,
  ageLabel,
} from "../../components/Compliance/findings.shared";
import { useAppSelector } from "../../hooks/redux-hooks";
import {
  useAddComplianceFindingCommentMutation,
  useGetComplianceAssigneesQuery,
  useGetComplianceFindingQuery,
  useUpdateComplianceFindingMutation,
} from "../../service/compliance.service";
import styles from "./compliance.module.scss";

const CLOSED_STATUSES: Status[] = ["closed", "false_positive"];

// "Today" / "1 day ago" / "3 days ago" — avoids the awkward "Today ago".
const relTime = (iso: string) => {
  const label = ageLabel(ageDays(iso));
  return label === "Today" ? "Today" : `${label} ago`;
};

const ComplianceFindingReview = () => {
  const { findingId } = useParams<{ findingId: string }>();
  const navigate = useNavigate();
  const { userInfo } = useAppSelector((state) => state.auth);
  const [comment, setComment] = useState("");

  const numericFindingId = Number(findingId);
  const isValidId = !Number.isNaN(numericFindingId);

  const isAdmin = !!userInfo?.show_compliance_dashboard && !!userInfo?.is_group_admin;
  const {
    data: finding,
    isLoading,
    isError,
  } = useGetComplianceFindingQuery(numericFindingId, {
    skip: !isAdmin || !isValidId,
  });
  const { data: assigneesData } = useGetComplianceAssigneesQuery(undefined, { skip: !isAdmin });
  const assignees = assigneesData?.assignees ?? [];

  const [updateFinding] = useUpdateComplianceFindingMutation();
  const [addComment, { isLoading: isPostingComment }] = useAddComplianceFindingCommentMutation();

  if (!isAdmin) {
    return <Navigate to="/home" replace />;
  }

  if (isLoading) {
    return (
      <div className={styles.compliance__container}>
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  if (isError || !finding) {
    return (
      <div className={styles.compliance__container}>
        <Flex vertical align="center" gap={12} style={{ padding: 80 }}>
          <Typography.Text style={{ color: "#6b7280" }}>Finding not found.</Typography.Text>
          <Button onClick={() => navigate("/compliance")}>Back to triage</Button>
        </Flex>
      </div>
    );
  }

  const sev = SEVERITY_META[finding.severity];
  const age = ageDays(finding.created_at);
  const breachedSla = age > SLA_DAYS[finding.severity] && !CLOSED_STATUSES.includes(finding.status);

  const onChangeStatus = async (s: Status) => {
    try {
      await updateFinding({ id: finding.id, status: s }).unwrap();
      message.success(`Marked as ${STATUS_META[s].label}`);
    } catch {
      message.error("Could not update status");
    }
  };

  const onAssign = async (assigneeId: number | null) => {
    try {
      await updateFinding({ id: finding.id, assignee_id: assigneeId ?? null }).unwrap();
      const name = assignees.find((a) => a.id === assigneeId)?.name;
      message.success(assigneeId ? `Assigned to ${name ?? "member"}` : "Unassigned");
    } catch {
      message.error("Could not update assignee");
    }
  };

  const onAddComment = async (body: string) => {
    try {
      await addComment({ id: finding.id, body }).unwrap();
      setComment("");
    } catch {
      message.error("Could not post comment");
    }
  };

  const onToggleArchive = async () => {
    try {
      await updateFinding({ id: finding.id, is_archived: !finding.is_archived }).unwrap();
      message.success(finding.is_archived ? "Restored from archive" : "Archived");
    } catch {
      message.error("Could not update finding");
    }
  };

  return (
    <div className={styles.compliance__container}>
      <Flex align="center" gap={8} wrap="wrap" style={{ marginBottom: 16 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/compliance")}
          size="small"
          style={{ color: "#6b7280" }}
        />
        <Breadcrumb
          items={[
            {
              title: (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/compliance");
                  }}
                >
                  Compliance Dashboard
                </a>
              ),
            },
            {
              title: (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/compliance");
                  }}
                >
                  Triage
                </a>
              ),
            },
            { title: `Finding #${finding.id}` },
          ]}
        />
      </Flex>

      <Card style={{ marginBottom: 24 }}>
        <Flex vertical gap={20}>
          <Flex vertical gap={8}>
            <Flex gap={8} align="center" wrap="wrap">
              <SeverityTag severity={finding.severity} />
              <StatusTag status={finding.status} />
              {breachedSla ? (
                <Tag
                  style={{
                    margin: 0,
                    color: TONE.danger.fg,
                    background: TONE.danger.bg,
                    borderColor: TONE.danger.border,
                  }}
                >
                  SLA breached
                </Tag>
              ) : null}
              {finding.is_archived ? <Tag>Archived</Tag> : null}
            </Flex>
            <Typography.Title level={3} style={{ margin: 0 }}>
              {finding.rule_name}
            </Typography.Title>
            <Typography.Text style={{ color: "#6b7280", fontSize: 13 }}>Rule {finding.rule_id}</Typography.Text>
          </Flex>

          <Card size="small" style={{ background: sev.bg, borderColor: sev.border }}>
            <Typography.Text style={{ color: sev.color, fontSize: 14, lineHeight: 1.6 }}>
              {finding.rationale}
            </Typography.Text>
          </Card>

          {finding.note_evidence ? (
            <Flex vertical gap={4}>
              <UppercaseLabel>Note evidence</UppercaseLabel>
              <Typography.Text italic style={{ color: "#374151", fontSize: 13 }}>
                “{finding.note_evidence}”
              </Typography.Text>
            </Flex>
          ) : null}

          <Row gutter={[24, 16]}>
            <Col xs={24} sm={12} md={6}>
              <UppercaseLabel>Clinician</UppercaseLabel>
              <div style={{ marginTop: 6 }}>
                <ClinicianAvatarCell name={finding.clinician_name} avatarSize={28} />
              </div>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <UppercaseLabel>Client</UppercaseLabel>
              <Typography.Title level={5} style={{ margin: "4px 0 0 0" }}>
                {finding.client_initials}
              </Typography.Title>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <UppercaseLabel>Note</UppercaseLabel>
              <Flex vertical style={{ marginTop: 4 }}>
                <Typography.Text strong style={{ fontSize: 13 }}>
                  {finding.note_title}
                </Typography.Text>
                <Typography.Text style={{ fontSize: 11, color: "#6b7280" }}>{finding.note_type}</Typography.Text>
              </Flex>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <UppercaseLabel>Age</UppercaseLabel>
              <Typography.Title
                level={5}
                style={{ margin: "4px 0 0 0", color: breachedSla ? TONE.danger.fg : undefined }}
              >
                {ageLabel(age)}
                {breachedSla ? (
                  <Typography.Text style={{ fontSize: 11, color: TONE.danger.fg, marginLeft: 8 }}>
                    SLA: {SLA_DAYS[finding.severity]}d
                  </Typography.Text>
                ) : null}
              </Typography.Title>
            </Col>
          </Row>

          <Row gutter={[24, 16]}>
            <Col xs={24} md={12}>
              <Flex vertical gap={8}>
                <UppercaseLabel>Status</UppercaseLabel>
                <Select<Status>
                  value={finding.status}
                  onChange={onChangeStatus}
                  style={{ width: "100%" }}
                  options={(Object.keys(STATUS_META) as Status[]).map((s) => ({
                    value: s,
                    label: (
                      <Flex align="center" gap={6}>
                        <span style={{ width: 8, height: 8, borderRadius: 999, background: STATUS_META[s].dot }} />
                        {STATUS_META[s].label}
                      </Flex>
                    ),
                  }))}
                />
              </Flex>
            </Col>
            <Col xs={24} md={12}>
              <Flex vertical gap={8}>
                <UppercaseLabel>Assignee</UppercaseLabel>
                <Select<number | null>
                  value={finding.assignee_id}
                  onChange={onAssign}
                  allowClear
                  placeholder="Unassigned"
                  style={{ width: "100%" }}
                  options={assignees.map((a) => ({ value: a.id, label: a.name }))}
                />
              </Flex>
            </Col>
          </Row>

          <Flex vertical gap={8}>
            <UppercaseLabel>Activity</UppercaseLabel>
            {finding.activities.length === 0 ? (
              <Typography.Text style={{ fontSize: 12, color: "#9ca3af" }}>No activity yet.</Typography.Text>
            ) : (
              <Flex vertical gap={8}>
                {finding.activities.map((a) =>
                  a.kind === "comment" ? (
                    <Card key={a.id} size="small" style={{ background: "#fafafa" }}>
                      <Flex justify="space-between" align="center" style={{ marginBottom: 4 }}>
                        <Typography.Text strong style={{ fontSize: 12 }}>
                          {a.actor_name}
                        </Typography.Text>
                        <Typography.Text style={{ fontSize: 11, color: "#9ca3af" }}>
                          {relTime(a.created_at)}
                        </Typography.Text>
                      </Flex>
                      <Typography.Text style={{ fontSize: 13, color: "#374151" }}>{a.body}</Typography.Text>
                    </Card>
                  ) : (
                    <Typography.Text key={a.id} style={{ fontSize: 12, color: "#6b7280" }}>
                      <strong>{a.actor_name}</strong> {a.body} · {relTime(a.created_at)}
                    </Typography.Text>
                  ),
                )}
              </Flex>
            )}
            <Input.TextArea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a comment or coaching note…"
              autoSize={{ minRows: 2, maxRows: 5 }}
            />
            <Flex justify="flex-end">
              <Button
                type="primary"
                icon={<CommentOutlined />}
                loading={isPostingComment}
                disabled={!comment.trim()}
                onClick={() => onAddComment(comment.trim())}
              >
                Post comment
              </Button>
            </Flex>
          </Flex>

          <Flex gap={8} wrap="wrap" style={{ borderTop: "1px solid #f0f0f0", paddingTop: 16 }}>
            <Button
              icon={<CheckCircleOutlined />}
              onClick={() => onChangeStatus("addressed")}
              disabled={finding.status === "addressed" || finding.status === "closed"}
            >
              Mark addressed
            </Button>
            <Button
              icon={<CheckCircleOutlined />}
              type="primary"
              onClick={() => onChangeStatus("closed")}
              disabled={finding.status === "closed"}
            >
              Close
            </Button>
            <Button
              icon={<StopOutlined />}
              onClick={() => onChangeStatus("false_positive")}
              disabled={finding.status === "false_positive"}
            >
              False positive
            </Button>
            <Button icon={finding.is_archived ? <UndoOutlined /> : <InboxOutlined />} onClick={onToggleArchive}>
              {finding.is_archived ? "Unarchive" : "Archive"}
            </Button>
          </Flex>
        </Flex>
      </Card>

      {finding.note_id ? (
        <Encounter
          noteId={finding.note_id}
          onFailRetry={() => navigate("/compliance")}
          failBackLabel="Back to triage"
        />
      ) : null}
    </div>
  );
};

export default ComplianceFindingReview;
